import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import TextField from "components/form/textField/TextField";
import Sheet from "components/sheet/Sheet";
import {
  canRemoveCartLineItem,
  canSetCartLineItemQuantity,
  minCartLineItemQuantity,
} from "helpers/cartLineItemPermissions";
import { isEmpty, isNumeric } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import React, { useCallback, useEffect, useState } from "react";
import { ICartLineItem, ICart, ICartTaxLine } from "redux/reducers/models/cart";
import { Select } from "components/select/index";
import "./editLineItemModal.scss";
import { IDiscount } from "api/rpc/2022-09/facilityAdmin/product/discount";
import Checkbox from "components/form/checkbox/Checkbox";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import Toggle from "components/form/toggle/Toggle";

export type TUpdatedLineItem = IEditState;

interface IProps {
  /**Boolean that determines if the modal is displayed*/
  open: boolean;
  /**The selected line item to be edited */
  lineItem: ICartLineItem;
  /**Function that runs when the cancel button is selected */
  onCancel: () => void;
  /**Function that runs when the 'Delete Item' button is selected */
  removeLineItem: () => Promise<void>;
  /**Function that runs when the 'Update' button is selected. The updated lineitem values are passed to this function */
  update: (updatedLineItem: TUpdatedLineItem) => Promise<void>;
  /**Function that runs when the 'Split Item' button is selected `(optional)` */
  openSplitItemModal?: () => void;
  /**Function that runs when the 'Modifier' button is selected */
  openModifierModal: (lineItem: ICartLineItem) => void;
  /**Function that runs when the 'Dynamic Combo' button is selected */
  openDynamicComboModal: (lineItem: ICartLineItem) => void;
  /**Boolean that determines if the 'Courses' dropdown is displayed `(optional)` */
  courses?: boolean;
  /**Cart currency */
  currency: string;
  cart?: ICart;
}

interface IEditState {
  quantity: string;
  price: string;
  note: string;
  courseId: number;
  discount: Partial<IDiscount>;
  customDiscountType: "fixed_amount" | "percent";
  customDiscountAmount: string;
  taxExemptionApplied: boolean;
}

/**
 * Component that allows user to edit the quantity, price, note, course and discount of a line item. As well as options to edit modifiers, split item and delete the item.
 * @param {IProps} props
 * @param {boolean} props.open Boolean that determines if the modal is displayed
 * @param {ICartLineItem} props.lineItem The selected line item to be edited
 * @param {() => void} props.onCancel Function that runs when the cancel button is selected
 * @param {() => Promise<void>} props.removeLineItem Function that runs when the 'Delete Item' button is selected
 * @param {(updatedLineItem: TUpdatedLineItem) => Promise<void>} props.update Function that runs when the update button is selected. The updated lineitem values are passed to this function
 * @param {() => void} props.openSplitItemModal Function that runs when the 'Split Item' button is selected `(optional)`
 * @param {() => void} props.openModifierModal Function that runs when the 'Modifier' button is selected
 * @param {() => void} props.openDynamicComboModal Function that runs when the 'Dynamic Combo' button is selected
 * @param {boolean} props.courses Boolean that determines if the 'Courses' dropdown is displayed `(optional)`
 * @param {string} props.currency Cart currency
 *
 */
export default function EditLineItemModal(props: IProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { getCurrencySymbol } = useCurrencyFacility();
  const {
    open,
    lineItem,
    onCancel,
    removeLineItem,
    update,
    openSplitItemModal,
    openModifierModal,
    openDynamicComboModal,
    courses,
    currency,
    cart,
  } = props;
  const [editState, setEditState] = useState<IEditState>({
    quantity: null,
    price: null,
    note: null,
    courseId: null,
    discount: { id: -1, title: t("None") },
    customDiscountType: "fixed_amount",
    customDiscountAmount: "",
    taxExemptionApplied: false,
  });
  const { Option } = Select;

  const permissions = useAppSelector(store => store?.authStore?.user?.permissions);
  const facilityStore = useAppSelector(store => store.facilityStore);

  useEffect(() => {
    if (open && lineItem) {
      setEditState({
        quantity: String(lineItem?.quantity),
        price: String(lineItem?.price?.toFixed(2)),
        note: lineItem?.note,
        courseId: lineItem.kitchen_meal_id ?? -1,
        discount: null,
        customDiscountType: "fixed_amount",
        customDiscountAmount: "",
        taxExemptionApplied: lineItem.tax_exemption_applied,
      });
    } else {
      setEditState({
        quantity: null,
        price: null,
        note: null,
        courseId: null,
        discount: { id: -1, title: t("None") },
        customDiscountType: "fixed_amount",
        customDiscountAmount: "",
        taxExemptionApplied: false,
      });
    }
  }, [open, lineItem]);

  const [isPriceAfterTax, setIsPriceAfterTax] = useState(false);

  const handlePriceAfterTaxToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsPriceAfterTax(checked);
    const taxRate = (lineItem?.tax_lines as unknown as ICartTaxLine[])?.reduce((sum, tax) => sum + tax.rate, 0) || 0;

    if (isNumeric(editState.price)) {
      const currentVal = parseFloat(editState.price);
      if (checked) {
        setEditState({ ...editState, price: (currentVal * (1 + taxRate)).toFixed(2) });
      } else {
        setEditState({ ...editState, price: (currentVal / (1 + taxRate)).toFixed(2) });
      }
    }
  };

  const customDiscountRef = useCallback(element => {
    if (element !== null) {
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const minimumQuantity = minCartLineItemQuantity(permissions, lineItem);

  function updateQuantity(type: "increment" | "decrement") {
    let currentQuantity = Number(editState?.quantity);
    if (type === "increment") {
      currentQuantity = !isEmpty(editState?.quantity) ? ++currentQuantity : 1;
    } else {
      currentQuantity = !isEmpty(editState?.quantity)
        ? currentQuantity > minimumQuantity
          ? --currentQuantity
          : currentQuantity
        : minimumQuantity;
    }
    setEditState(prevState => ({ ...prevState, quantity: String(currentQuantity) }));
  }

  function quantityOnBlur() {
    if (isEmpty(editState?.quantity)) {
      setEditState(prevState => ({ ...prevState, quantity: String(lineItem?.quantity ?? 1) }));
      return;
    }

    const currentQuantity = Number(editState?.quantity);
    if (currentQuantity < minimumQuantity) {
      setEditState(prevState => ({ ...prevState, quantity: String(minimumQuantity) }));
    }
  }

  function priceOnBlur() {
    const currentPrice = Number(editState?.price);
    setEditState(prevState => ({
      ...prevState,
      price: currentPrice < 0 || isEmpty(editState?.price) ? String(0) : String(currentPrice?.toFixed(2)),
    }));
  }

  function openModifiers(lineItem: ICartLineItem) {
    void onCancel();
    void openModifierModal(lineItem);
  }

  const disableRemoveLineItem = !canRemoveCartLineItem(permissions, lineItem);

  function openDynamicCombo(lineItem: ICartLineItem) {
    void onCancel();
    void openDynamicComboModal(lineItem);
  }

  return (
    <Sheet
      title={t("Update Item")}
      open={open}
      size="medium"
      onCancel={() => onCancel()}
      onOk={() => {
        const newQuantity = Number(editState.quantity);
        if (!canSetCartLineItemQuantity(permissions, newQuantity, lineItem)) {
          dispatch(showError(t("You do not have permission to remove items from the cart.")));
          return;
        }

        if (newQuantity === 0) {
          void removeLineItem();
          return;
        }

        const taxRate = (lineItem?.tax_lines as unknown as ICartTaxLine[])?.reduce((sum, tax) => sum + tax.rate, 0) || 0;
        const rawPrice = isPriceAfterTax ? (parseFloat(editState.price) / (1 + taxRate)) : parseFloat(editState.price);
        const submittedPrice = parseFloat(rawPrice.toFixed(2)).toString();

        void update({
          ...editState,
          price: submittedPrice,
          discount: editState.discount?.id === -1 ? null : editState?.discount,
          courseId: editState?.courseId === -1 ? null : editState?.courseId,
        });
      }}
      okText={
        Number(editState.quantity) === 0 && canRemoveCartLineItem(permissions, lineItem) ? t("Remove") : t("Update")
      }
      closable
      okDisabled={
        (editState.discount?.custom && !editState.customDiscountAmount) ||
        !canSetCartLineItemQuantity(permissions, Number(editState.quantity), lineItem)
      }
    >
      <div className="edit-line-item-modal">
        <div className="edit-item-header-container">
          <div className="edit-item-title-container">
            <h1>{lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product_title}</h1>
            {lineItem?.product_title !== lineItem?.variant_title && <p>{lineItem?.variant_title}</p>}
          </div>
          <div className="edit-item-icons-container">
            {openSplitItemModal && !lineItem?.parent_id && (
              <Button
                onClick={() => openSplitItemModal()}
                type="text"
                size="medium"
                style={{ color: "#3D5FA0" }}
                disabled={!!lineItem?.parent_id}
              >
                <FontAwesomeIcon size="1x" icon={["far", "split"]} className="mr-2" /> Split Item
              </Button>
            )}
            {!lineItem?.parent_id && !!lineItem?.product?.has_modifiers && (
              <Button
                onClick={() => openModifiers(lineItem)}
                type="text"
                size="medium"
                style={{ color: "#3D5FA0" }}
                disabled={!!lineItem?.parent_id || !lineItem?.product?.has_modifiers}
              >
                <FontAwesomeIcon size="1x" icon={["far", "salt-shaker"]} className="mr-2" /> Modify
              </Button>
            )}
            {lineItem?.product?.type === "Combo" && !!lineItem?.product?.dynamic_combo && (
              <Button onClick={() => openDynamicCombo(lineItem)} type="text" size="medium" style={{ color: "#3D5FA0" }}>
                <FontAwesomeIcon size="1x" icon={["far", "pen"]} className="mr-2" /> {t('Dynamic Combo')}
              </Button>
            )}
            <Button type="error-outline" size="medium" disabled={disableRemoveLineItem} onClick={removeLineItem}>
              <FontAwesomeIcon size="1x" icon={["far", "trash-can"]} className="mr-2" /> {t('Delete Item')}
            </Button>
          </div>
        </div>

        <FormLayout>
          <FormLayout.Group>
            <Input
              label={t("Quantity")}
              placeholder={t("Quantity")}
              value={editState.quantity ?? ""}
              type="number"
              min={minimumQuantity}
              className="edit-item-quantity-input"
              onBlur={quantityOnBlur}
              onChange={e => setEditState(prevState => ({ ...prevState, quantity: e.target.value }))}
              leadingButtons={[
                <Button
                  key={1}
                  type="secondary"
                  onClick={() => updateQuantity("decrement")}
                  disabled={Number(editState.quantity) <= minimumQuantity}
                >
                  <FontAwesomeIcon icon={["far", "minus"]} size="1x" />
                </Button>,
              ]}
              trailingButtons={[
                <Button
                  key={2}
                  type="secondary"
                  onClick={() => updateQuantity("increment")}
                >
                  <FontAwesomeIcon icon={["far", "plus"]} size="1x" />
                </Button>,
              ]}
            />
            <Input
              label={t("Price")}
              placeholder={t("Price")}
              type="number"
              value={editState?.price ?? ""}
              onChange={e => setEditState(prevState => ({ ...prevState, price: e.target.value }))}
              onBlur={priceOnBlur}
              disabled={!lineItem?.product?.dynamic_price && (!permissions?.orders_edit_line_item_price || lineItem?.product?.price_locked)}
              prefix={getCurrencySymbol(currency) ?? "$"}
              autoFocus={true}
            />
          </FormLayout.Group>
          {lineItem?.tax_lines?.length > 0 ? (
            <FormLayout.Group>
              <div></div>
              <Toggle
                label={t("Enter Price After Tax")}
                checked={isPriceAfterTax}
                onChange={handlePriceAfterTaxToggle}
              />
            </FormLayout.Group>
          ) : null}
          {!lineItem?.parent_id && (
            <FormLayout.Group>
              <TextField
                label={t("Note")}
                placeholder={t("Notes")}
                value={editState?.note ?? ""}
                onChange={e => setEditState(prevState => ({ ...prevState, note: e.target.value }))}
              />
            </FormLayout.Group>
          )}
          {facilityStore?.kitchenCourses?.length > 0 && !lineItem?.parent_id && courses && (
            <FormLayout.Group>
              <Select
                label={t("Course")}
                defaultValue={lineItem?.kitchen_meal_id ?? -1}
                onChange={value => setEditState(prev => ({ ...prev, courseId: value }))}
                usePortal
              >
                {[
                  { id: -1, title: t("No Course") },
                  ...facilityStore.kitchenCourses.sort((a, b) => a.title.localeCompare(b.title)),
                ]?.map(course => {
                  return (
                    <Option key={course?.id} value={course?.id}>
                      {course?.title}
                    </Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
          )}
          <FormLayout.Group>
            <Select
              label={t("Discount")}
              onChange={(id, discount) => setEditState(prev => ({ ...prev, discount }))}
              usePortal
              disabled={!permissions?.orders_apply_discount}
              defaultValue={-1}
            >
              {[{ id: -1, title: t("None"), application: "line_item" }, ...facilityStore.discountOptions]
                ?.filter((filteredDiscount: IDiscount) => filteredDiscount?.application === "line_item")
                ?.map((discount: IDiscount) => {
                  return (
                    <Option key={discount?.id} value={discount?.id} extraValues={discount}>
                      {discount?.title}
                    </Option>
                  );
                })}
            </Select>
          </FormLayout.Group>
          {editState?.discount && editState?.discount?.custom && (
            <FormLayout.Group alignCenter>
              <Input
                label={editState.customDiscountType === "fixed_amount" ? "Fixed Amount" : "Percent"}
                value={editState.customDiscountAmount}
                prefix={editState.customDiscountType === "fixed_amount" ? "$" : undefined}
                suffix={editState.customDiscountType === "percent" ? "%" : undefined}
                type="number"
                onChange={e => setEditState(prevState => ({ ...prevState, customDiscountAmount: e.target.value }))}
              />
              <div className="edit-item-custom-discount" ref={customDiscountRef}>
                <Checkbox
                  label={t("Fixed Amount")}
                  size="medium"
                  circle
                  fullWidth
                  checked={editState.customDiscountType === "fixed_amount"}
                  onChange={() => setEditState(prevState => ({ ...prevState, customDiscountType: "fixed_amount" }))}
                />
                <Checkbox
                  label={t("Percent")}
                  size="medium"
                  circle
                  fullWidth
                  checked={editState.customDiscountType === "percent"}
                  onChange={() => setEditState(prevState => ({ ...prevState, customDiscountType: "percent" }))}
                />
              </div>
            </FormLayout.Group>
          )}
          {lineItem?.product?.tax_exempt && cart?.customer?.tax_exempt ? (
            <FormLayout.Group>
              <Toggle
                label={t("Tax Exempt")}
                checked={editState.taxExemptionApplied}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditState({ ...editState, taxExemptionApplied: e.target.checked })
                }
              />
            </FormLayout.Group>
          ) : null}
        </FormLayout>
      </div>
    </Sheet>
  );
}
